import React, { useEffect, useState } from "react";
import FeaturedArticleCard from "./FeaturedArticleCard";
import NewsList from "./NewsList";
import { apiFetch } from "../utils/api";

// Parse "yyyy-MM-dd hh:mm:ss" safely into local Date
function parseSqlDate(sqlDate) {
  if (!sqlDate) return null;

  // "2026-01-19 14:32:00" → "2026-01-19T14:32:00"
  const isoLike = sqlDate.replace(" ", "T");
  const date = new Date(isoLike);

  return isNaN(date.getTime()) ? null : date;
}

export default function HomePage({
  user,
  searchQuery,
  selectedCategory,
  selectedAuthor,
  startDate,
  endDate,
  setCategories,
  setAuthors
  }) {
  const [articles, setArticles] = useState(null);

  const handleArticleDeleted = async (id) => {
    try {
      const res = await apiFetch(`/articles/${id}`, {
        method: "DELETE"
      });

      if (!res.ok) throw new Error("Delete failed");

      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete article.");
    }
  };

  // -----------------------------
  // Data Fetch
  // -----------------------------
  useEffect(() => {
    let isMounted = true;

    const loadArticles = async () => {
      try {
        const res = await apiFetch("/articles");
        const data = await res.json();

        const normalizedArticles = data.map(article => ({
          ...article,
          // 🔐 hidden, machine-safe date for filtering
          filterDate: parseSqlDate(article.created_at)
        }));

        if (!isMounted) return;

        setArticles(normalizedArticles);

        setCategories([
          ...new Set(normalizedArticles.map(a => a.category).filter(Boolean))
        ]);

        setAuthors([
          ...new Set(normalizedArticles.map(a => a.author).filter(Boolean))
        ]);
      } catch (err) {
        console.error("Failed to load articles", err);
        if (isMounted) setArticles([]);
      }
    };

    // 🔁 Initial load
    loadArticles();

    // 🔁 Poll every 30 seconds
    const interval = setInterval(loadArticles, 30000);

    // 🧹 Cleanup
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);


  // -----------------------------
  // Loading
  // -----------------------------
  if (articles === null) {
    return <p className="loading">Loading articles...</p>;
  }

  // -----------------------------
  // Filtering
  // -----------------------------
  const filteredArticles = articles.filter(article => {
    const matchesText =
      !searchQuery ||
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      article.category === selectedCategory;

    const matchesAuthor =
      selectedAuthor === "all" ||
      article.author === selectedAuthor;

    let matchesDate = true;
    const articleDate = article.filterDate;

    if (startDate) {
      const start = new Date(startDate + "T00:00:00");
      if (!articleDate) return false;
      matchesDate = articleDate >= start;
    }

    if (endDate) {
      const end = new Date(endDate + "T23:59:59");
      if (!articleDate) return false;
      matchesDate = matchesDate && articleDate <= end;
    }

    return (
      matchesText &&
      matchesCategory &&
      matchesAuthor &&
      matchesDate
    );
  });

  if (filteredArticles.length === 0) {
    return <p>No articles match your search.</p>;
  }

  const [featuredArticle, ...restArticles] = filteredArticles;

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <main>
      <div className="featured-wrapper">
        <FeaturedArticleCard
          article={featuredArticle}
          user={user}
          onDeleted={handleArticleDeleted}
        />
      </div>
      <NewsList 
        articles={restArticles} 
        user={user}
      />
    </main>
  );
}
